import { Helmet } from 'react-helmet-async';

const BASE_URL = 'https://dockclaw.com';
const DEFAULT_OG_IMAGE = `${BASE_URL}/logo-200.png`;
const SITE_NAME = 'DockClaw';

/**
 * SEOHead — drop-in <head> manager for every page.
 *
 * @param {object}  props
 * @param {string}  props.title       - Page title (rendered as "title | DockClaw")
 * @param {string}  props.description - Meta description (max ~160 chars)
 * @param {string}  [props.keywords]  - Comma-separated keywords
 * @param {string}  props.path        - Path portion of canonical URL (e.g. "/pricing")
 * @param {string}  [props.ogImage]   - Full URL to Open Graph image
 * @param {string}  [props.ogType]    - Open Graph type (default: "website")
 * @param {object|Array} [props.schema] - JSON-LD structured data object or array
 * @param {boolean} [props.noindex]   - If true, adds noindex,nofollow
 */
export default function SEOHead({
  title,
  description,
  keywords,
  path = '/',
  ogImage,
  ogType = 'website',
  schema,
  noindex = false,
}) {
  // Avoid double-suffixing if title already ends with the site name
  const alreadySuffixed = title && title.endsWith(`| ${SITE_NAME}`);
  const fullTitle = title
    ? (alreadySuffixed ? title : `${title} | ${SITE_NAME}`)
    : SITE_NAME;
  const canonicalUrl = `${BASE_URL}${path}`;
  const image = ogImage || DEFAULT_OG_IMAGE;

  return (
    <Helmet>
      {/* Primary */}
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonicalUrl} />

      {/* Robots — always emit to override noindex default in SPA shell */}
      <meta name="robots" content={noindex ? 'noindex,nofollow' : 'index,follow'} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={image} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={SITE_NAME} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={image} />

      {/* JSON-LD structured data */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(Array.isArray(schema) ? schema : schema, null, 2)}
        </script>
      )}
    </Helmet>
  );
}
